import { useQuery } from '@tanstack/react-query';

import { DropdownMenu } from '../cdl/DropdownMenu/DropdownMenu';
import { TableBuilder } from '../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../cdl/Text/Text';
import { useToasts } from '../cdl/Toast/useToasts';
import { FailedRequisitionImport } from '../common/api/clients/mariapps.api';
import { queries } from '../common/api/queryKeys/queries';
import { translate } from '../common/helpers/translate.helper';
import { InfoPopover } from '../common/InfoPopover/InfoPopover';
import { LoadingIndicator } from '../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../common/ui/Box';

import { useMariAppsDeleteFailedRequisition } from './useMariAppsDeleteFailedRequisition';
import { useMariAppsFailedRequisitions } from './useMariAppsFailedRequisitions';
import { useMariAppsImportRequisition } from './useMariAppsImportRequisition';
import { useMariAppsMarkNonRetryable } from './useMariAppsMarkNonRetryable';

interface FailedRequisitionsTableProps {
    groupId?: string;
}

const getCustomerGroupName = (
    customerGroupId: string,
    customerGroups: Array<{ id: string; name: string }> | undefined
): string => {
    if (!customerGroups || !Array.isArray(customerGroups)) {
        return customerGroupId;
    }
    const group = customerGroups.find((g) => g.id === customerGroupId);
    return group?.name || customerGroupId;
};

export const FailedRequisitionsTable = ({ groupId }: FailedRequisitionsTableProps) => {
    const { data, isPending, isError, refetch } = useMariAppsFailedRequisitions(groupId);
    const deleteFailedRequisition = useMariAppsDeleteFailedRequisition();
    const markNonRetryable = useMariAppsMarkNonRetryable();
    const importRequisition = useMariAppsImportRequisition();
    const { addToast, addErrorToast } = useToasts();

    const failedRequisitions = data?.failedRequisitions || [];

    const { data: customerGroupsData } = useQuery(queries.customerGroups.list());

    const handleRetry = (failedRequisition: FailedRequisitionImport) => {
        importRequisition.mutate(
            {
                groupId: failedRequisition.customerGroupId,
                requisitionNumber: failedRequisition.requisitionNumber,
            },
            {
                onSuccess: () => {
                    addToast(translate('integrationsOverview.retryTriggered'));
                    refetch();
                },
                onError: (error: any) => {
                    const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                    addErrorToast(errorMessage);
                },
            }
        );
    };

    const handleDelete = (id: string) => {
        deleteFailedRequisition.mutate(id, {
            onSuccess: () => {
                addToast(translate('integrationsOverview.failedRequisitionDeleted'));
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                addErrorToast(errorMessage);
            },
        });
    };

    const handleMarkNonRetryable = (id: string) => {
        markNonRetryable.mutate(id, {
            onSuccess: () => {
                addToast(translate('integrationsOverview.failedRequisitionMarkedNonRetryable'));
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                addErrorToast(errorMessage);
            },
        });
    };

    if (isPending) {
        return <LoadingIndicator />;
    }

    if (isError) {
        return <Text>{translate('integrationsOverview.unknownError')}</Text>;
    }

    if (failedRequisitions.length === 0) {
        return <Text>{translate('integrationsOverview.noFailedRequisitions')}</Text>;
    }

    return (
        <Box width="100%">
            <TableBuilder<FailedRequisitionImport> data={failedRequisitions}>
                <TableBuilderColumn<FailedRequisitionImport> header={translate('integrationsOverview.groupLabel')}>
                    {(row) => getCustomerGroupName(row.customerGroupId, customerGroupsData)}
                </TableBuilderColumn>
                <TableBuilderColumn<FailedRequisitionImport>
                    header={translate('integrationsOverview.requisitionNumberLabel')}
                >
                    {(row) => row.requisitionNumber}
                </TableBuilderColumn>
                <TableBuilderColumn<FailedRequisitionImport>
                    header={translate('integrationsOverview.failureReasonLabel')}
                >
                    {(row) => row.failureReason}
                </TableBuilderColumn>
                <TableBuilderColumn<FailedRequisitionImport>
                    header={translate('integrationsOverview.errorMessageLabel')}
                >
                    {(row) => (
                        <Box maxWidth="300px" style={{ wordBreak: 'break-word' }}>
                            {row.errorMessage}
                        </Box>
                    )}
                </TableBuilderColumn>
                <TableBuilderColumn<FailedRequisitionImport> header={translate('integrationsOverview.retryCountLabel')}>
                    {(row) => row.retryCount.toString()}
                </TableBuilderColumn>
                <TableBuilderColumn<FailedRequisitionImport>
                    header={
                        <Box display="flex" alignItems="center" gap={4}>
                            {translate('integrationsOverview.actionsLabel')}
                            <InfoPopover>
                                <Box display="flex" flexDirection="column" gap={4}>
                                    <Text variant="small" fontWeight={700}>
                                        {translate('integrationsOverview.retryButton')}
                                    </Text>
                                    <Text variant="small">{translate('integrationsOverview.retryButtonTooltip')}</Text>
                                    <Text variant="small" fontWeight={700} marginTop={8}>
                                        {translate('integrationsOverview.markNonRetryableButton')}
                                    </Text>
                                    <Text variant="small">
                                        {translate('integrationsOverview.markNonRetryableButtonTooltip')}
                                    </Text>
                                    <Text variant="small" fontWeight={700} marginTop={8}>
                                        {translate('integrationsOverview.deleteButton')}
                                    </Text>
                                    <Text variant="small">{translate('integrationsOverview.deleteButtonTooltip')}</Text>
                                </Box>
                            </InfoPopover>
                        </Box>
                    }
                >
                    {(row) => {
                        const options = [
                            {
                                label: translate('integrationsOverview.retryButton'),
                                onSelect: () => handleRetry(row),
                                disabled: !row.retryable || importRequisition.isPending,
                            },
                            {
                                label: translate('integrationsOverview.markNonRetryableButton'),
                                onSelect: () => handleMarkNonRetryable(row.id),
                                disabled: markNonRetryable.isPending,
                            },
                            {
                                label: translate('integrationsOverview.deleteButton'),
                                onSelect: () => handleDelete(row.id),
                                disabled: deleteFailedRequisition.isPending,
                                destructive: true,
                            },
                        ];

                        return <DropdownMenu options={options} align="end" />;
                    }}
                </TableBuilderColumn>
            </TableBuilder>
        </Box>
    );
};
